import React, { useState } from 'react';
import { 
  BarChart3, Clock, Eye, ThumbsUp, Bookmark,
  TrendingUp, Users, Star, Award, Target, Zap,
  ChevronRight, ExternalLink, BookOpen, Brain,
  Calendar, Tag, User, MessageCircle, ArrowRight
} from 'lucide-react';
import { EnhancedAnalysis } from '../../types/enhancedAnalysis';

interface SidebarStatsProps {
  analysis: EnhancedAnalysis;
  readingProgress: number;
}

interface StatCardProps {
  icon: React.ComponentType<any>;
  label: string;
  value: string | number;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, color, trend }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        {trend && (
          <div className={`flex items-center space-x-1 text-xs ${
            trend.isPositive ? 'text-green-600' : 'text-red-600'
          }`}>
            <TrendingUp className={`w-3 h-3 ${trend.isPositive ? '' : 'rotate-180'}`} />
            <span>{Math.abs(trend.value)}%</span>
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
    </div>
  );
};

interface RelatedCaseCardProps {
  case: {
    id: string;
    title: string;
    summary: string;
    category: string;
    readingTime: number;
    similarity: number;
    slug: string;
  };
  onClick: () => void;
}

const RelatedCaseCard: React.FC<RelatedCaseCardProps> = ({ case: relatedCase, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 text-sm leading-tight mb-1 group-hover:text-blue-600 transition-colors">
            {relatedCase.title.length > 50 ? `${relatedCase.title.slice(0, 50)}...` : relatedCase.title}
          </h4>
          <p className="text-xs text-gray-600 line-clamp-2">
            {relatedCase.summary.slice(0, 80)}...
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all flex-shrink-0 ml-2" />
      </div>
      
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center space-x-3">
          <span className="flex items-center space-x-1">
            <Tag className="w-3 h-3" />
            <span>{relatedCase.category}</span>
          </span>
          <span className="flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>{relatedCase.readingTime}分钟</span>
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span>{Math.round(relatedCase.similarity * 100)}% 相似</span>
        </div>
      </div>
    </div>
  );
};

const SidebarStats: React.FC<SidebarStatsProps> = ({ analysis, readingProgress }) => {
  const [activeSection, setActiveSection] = useState<'stats' | 'related' | 'tags'>('stats');

  // 模拟相关案例数据
  const relatedCases = [
    {
      id: '1',
      title: '科技巨头的战略转型分析',
      summary: '深入分析科技公司如何应对市场变化进行战略调整',
      category: '商业战略',
      readingTime: 8,
      similarity: 0.85,
      slug: 'tech-giant-strategy'
    },
    {
      id: '2',
      title: '新兴市场的机遇与挑战',
      summary: '探讨新兴市场的发展潜力和面临的主要挑战',
      category: '市场分析',
      readingTime: 12,
      similarity: 0.72,
      slug: 'emerging-market-analysis'
    },
    {
      id: '3',
      title: '数字化转型的成功要素',
      summary: '分析企业数字化转型过程中的关键成功因素',
      category: '数字化',
      readingTime: 10,
      similarity: 0.68,
      slug: 'digital-transformation'
    }
  ];

  const handleRelatedCaseClick = (caseItem: any) => {
    window.open(`/analysis/${caseItem.slug}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* 阅读进度卡片 */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
            阅读进度
          </h3>
          <span className="text-sm font-medium text-blue-600">
            {Math.round(readingProgress)}%
          </span>
        </div>
        
        <div className="w-full h-3 bg-blue-200 rounded-full overflow-hidden mb-4">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500 ease-out"
            style={{ width: `${readingProgress}%` }}
          />
        </div>
        
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>继续阅读解锁更多洞察</span>
          <span>{Math.ceil((100 - readingProgress) / 20)} 分钟剩余</span>
        </div>
      </div>

      {/* 标签页导航 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {[
            { id: 'stats', name: '统计', icon: BarChart3 },
            { id: 'related', name: '相关', icon: BookOpen },
            { id: 'tags', name: '标签', icon: Tag }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id as any)}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 text-sm font-medium transition-colors ${
                  activeSection === tab.id
                    ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>

        {/* 统计信息 */}
        {activeSection === 'stats' && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                icon={Eye}
                label="阅读量"
                value={analysis.socialMetrics.views.toLocaleString()}
                color="bg-blue-500"
                trend={{ value: 12, isPositive: true }}
              />
              <StatCard
                icon={ThumbsUp}
                label="点赞数"
                value={analysis.socialMetrics.likes}
                color="bg-green-500"
                trend={{ value: 8, isPositive: true }}
              />
              {/* 移除分享数统计 */}
              <StatCard
                icon={Bookmark}
                label="收藏数"
                value={analysis.socialMetrics.bookmarks}
                color="bg-orange-500"
                trend={{ value: 5, isPositive: true }}
              />
            </div>

            {/* 详细统计 */}
            <div className="pt-4 border-t border-gray-200 space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">字数统计</span>
                <span className="font-medium text-gray-900">{analysis.wordCount.toLocaleString()} 字</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">阅读时长</span>
                <span className="font-medium text-gray-900">约 {analysis.readingTime} 分钟</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">参与度</span>
                <span className="font-medium text-gray-900">
                  {Math.round(analysis.socialMetrics.engagementRate * 100)}%
                </span>
              </div>
            </div>

            {/* 作者信息 */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {analysis.author.name.charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{analysis.author.name}</div>
                  <div className="text-sm text-gray-500">{analysis.author.title}</div>
                </div>
              </div>
              
              {analysis.author.expertise.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {analysis.author.expertise.slice(0, 3).map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 相关案例 */}
        {activeSection === 'related' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900">相关案例推荐</h4>
              <span className="text-xs text-gray-500">{relatedCases.length} 个推荐</span>
            </div>
            
            <div className="space-y-3">
              {relatedCases.map((caseItem) => (
                <RelatedCaseCard
                  key={caseItem.id}
                  case={caseItem}
                  onClick={() => handleRelatedCaseClick(caseItem)}
                />
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <button className="w-full flex items-center justify-center space-x-2 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors">
                <span>查看更多相关案例</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* 标签云 */}
        {activeSection === 'tags' && (
          <div className="p-6">
            <h4 className="font-medium text-gray-900 mb-4">相关标签</h4>
            
            <div className="flex flex-wrap gap-2 mb-6">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full font-medium">
                {analysis.category}
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                深度分析
              </span>
              <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                {analysis.methodology.framework}
              </span>
              <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">
                思维训练
              </span>
              <span className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full">
                案例研究
              </span>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                商业洞察
              </span>
            </div>

            {/* 热门标签 */}
            <div className="pt-4 border-t border-gray-200">
              <h5 className="text-sm font-medium text-gray-900 mb-3">热门标签</h5>
              <div className="space-y-2">
                {[
                  { name: '战略分析', count: 156 },
                  { name: '市场研究', count: 134 },
                  { name: '商业模式', count: 98 },
                  { name: '竞争分析', count: 87 }
                ].map((tag, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <button className="text-sm text-gray-700 hover:text-blue-600 transition-colors">
                      #{tag.name}
                    </button>
                    <span className="text-xs text-gray-500">{tag.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 快速操作 */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">快速操作</h3>
        <div className="space-y-3">
          <button className="w-full flex items-center space-x-3 p-3 text-left rounded-lg hover:bg-gray-50 transition-colors group">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <Brain className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">尝试分析</div>
              <div className="text-sm text-gray-500">使用相同方法论</div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
          </button>
          
          <button className="w-full flex items-center space-x-3 p-3 text-left rounded-lg hover:bg-gray-50 transition-colors group">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
              <BookOpen className="w-4 h-4 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">学习资源</div>
              <div className="text-sm text-gray-500">相关学习材料</div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors" />
          </button>
          
          <button className="w-full flex items-center space-x-3 p-3 text-left rounded-lg hover:bg-gray-50 transition-colors group">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <MessageCircle className="w-4 h-4 text-purple-600" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">讨论交流</div>
              <div className="text-sm text-gray-500">加入社区讨论</div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-purple-600 transition-colors" />
          </button>
        </div>
      </div>

      {/* 趋势指标 */}
      {analysis.socialMetrics.trending && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
          <div className="flex items-center space-x-2 mb-3">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="font-semibold text-orange-900">热门分析</div>
              <div className="text-sm text-orange-700">正在热议中</div>
            </div>
          </div>
          <div className="text-sm text-orange-800">
            这篇分析的热度比平均水平高出 {Math.round((analysis.socialMetrics.trendingScore || 1.5) * 100)}%，
            快来参与讨论吧！
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarStats;